import { z } from "zod";

import { OrderDirection } from "@/constants/orderDirection";

import type { IdType } from "./-query/type";

export const TabKey = {
  REGISTERED: "registered",
  TOLERATED: "tolerated",
  BLOCKED: "blocked",
  ALL: "all",
} as const;

export type TabKey = (typeof TabKey)[keyof typeof TabKey];
export const TabKeySchema = z.enum([
  "registered",
  "tolerated",
  "blocked",
  "all",
]);

export const ColumnKey = {
  TIMESTAMP: "timestamp",
  WHO_ACCESSED: "whoAccessed",
  IDENTITY: "identity",
  DEVICE: "device",
  ACCESSED_TO: "accessedTo",
  ACTION: "action",
} as const;

export type ColumnKey = (typeof ColumnKey)[keyof typeof ColumnKey];

/**
 * ソート可能なカラムキー
 * NOTE: useTable.ts の toOrderBy 関数と同期させる必要があります
 */
export const SortableColumnKey = {
  TIMESTAMP: ColumnKey.TIMESTAMP,
  WHO_ACCESSED: ColumnKey.WHO_ACCESSED,
  IDENTITY: ColumnKey.IDENTITY,
  ACCESSED_TO: ColumnKey.ACCESSED_TO,
  ACTION: ColumnKey.ACTION,
} as const;

export type SortableColumnKey =
  (typeof SortableColumnKey)[keyof typeof SortableColumnKey];

const SortableColumnKeySchema = z.enum([
  SortableColumnKey.TIMESTAMP,
  SortableColumnKey.WHO_ACCESSED,
  SortableColumnKey.IDENTITY,
  SortableColumnKey.ACCESSED_TO,
  SortableColumnKey.ACTION,
]) satisfies z.ZodType<SortableColumnKey>;

/**
 * ID特定方法のZodスキーマ
 * NOTE: APIの型（ListAccessesQuery["idTypeIn"]）と同期させる必要があります
 * @see IdType in ./-query/type.ts
 */
const IdTypeSchema = z.enum([
  "PERSONAL_EMAIL",
  "PERSONAL_PHONE",
  "PERSONAL_SNS_ID",
  "WORKER_EMAIL",
  "WORKER_MAIN_EMAIL",
  "WORKER_SUB_EMAIL",
  "WORKER_MAIN_PHONE",
  "WORKER_SUB_PHONE",
  "EMPLOYEE_NUMBER",
]) satisfies z.ZodType<IdType>;

export const SearchSchema = z.object({
  tab: TabKeySchema.default(TabKey.REGISTERED),
  pageIndex: z.number().default(0),
  pageSize: z.number().default(100),
  freeText: z.string().optional(),
  sortColumn: SortableColumnKeySchema.optional(),
  sortOrder: z.nativeEnum(OrderDirection).optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  /** VisitUserIDでフィルタ */
  visitUserIdIn: z.array(z.string()).optional(),
  /** ID特定方法でフィルタ（ProxyPersonMatchType） */
  idTypeIn: z.array(IdTypeSchema).optional(),
  /** TraceableAppIDでフィルタ（Software） */
  traceableAppIdIn: z.array(z.string()).optional(),
});

export type SearchParams = z.infer<typeof SearchSchema>;
